import React from 'react';

type Size = 'small' | 'medium' | 'large';
type Variant = 'light' | 'dark' | 'violet';

interface LogoInsaiteProps {
  size?: Size;
  position?: 'fixed' | 'relative';
  variant?: Variant;
  aiOnly?: boolean; // Muestra solo el símbolo de la marca (ícono)
}

/**
 * SVG Logo genérico "AI Recruiter" con ícono de brackets < / >.
 * Reemplaza las imágenes PNG de marca para la versión Friends & Family.
 */
export function LogoInsaite({
  size = 'medium',
  position = 'relative',
  variant = 'light',
  aiOnly = false
}: LogoInsaiteProps) {
  const className = `logo-insaite-container logo-${size} logo-${position}`;
  const style = position === 'fixed' ? { position: 'fixed' as const, top: '4rem', right: '2rem' } : undefined;

  // Determinar colores según variant
  let primaryColor = '#FFFFFF';
  let accentColor = '#85BEFF';
  if (variant === 'dark') {
    primaryColor = '#1F2937';
    accentColor = '#0069E0';
  } else if (variant === 'violet') {
    primaryColor = '#5b21b6';
    accentColor = '#7c3aed';
  }

  // Tamaños del ícono según size
  const iconSize = size === 'small' ? 28 : size === 'medium' ? 36 : 48;

  // LOGO CONTRAÍDO (solo ícono)
  if (aiOnly) {
    return React.createElement(
      'div',
      {
        className: `${className} ai-only`,
        style,
      },
      React.createElement(
        'svg',
        {
          width: iconSize,
          height: iconSize,
          viewBox: '0 0 40 40',
          fill: 'none',
          xmlns: 'http://www.w3.org/2000/svg',
          className: 'logo-insaite',
          'aria-label': 'AI Recruiter Icon',
        },
        // Fondo circular
        React.createElement('circle', {
          cx: '20', cy: '20', r: '18',
          fill: primaryColor,
          opacity: variant === 'light' ? '0.15' : '0.1',
        }),
        // Brackets < / >
        React.createElement('path', {
          d: 'M15 13L8 20L15 27',
          stroke: primaryColor,
          strokeWidth: '2.5',
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }),
        React.createElement('path', {
          d: 'M25 13L32 20L25 27',
          stroke: primaryColor,
          strokeWidth: '2.5',
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }),
        // Barra diagonal central
        React.createElement('path', {
          d: 'M22 12L18 28',
          stroke: accentColor,
          strokeWidth: '2',
          strokeLinecap: 'round',
        })
      )
    );
  }

  // LOGO COMPLETO con texto "AI Recruiter"
  const fullHeight = size === 'small' ? 36 : size === 'medium' ? 48 : 64;
  const fullWidth = size === 'small' ? 140 : size === 'medium' ? 180 : 240;
  const fontSize = size === 'small' ? '14' : size === 'medium' ? '18' : '24';
  const iconScale = size === 'small' ? 0.6 : size === 'medium' ? 0.75 : 1;

  return React.createElement(
    'div',
    { className: `${className} logo-full-brackets-img`, style },
    React.createElement(
      'svg',
      {
        width: fullWidth,
        height: fullHeight,
        viewBox: `0 0 ${fullWidth} ${fullHeight}`,
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg',
        'aria-label': 'AI Recruiter Logo',
        style: {
          display: 'block',
        }
      },
      // Ícono de brackets
      React.createElement(
        'g',
        { transform: `translate(4, ${fullHeight / 2 - 15 * iconScale}) scale(${iconScale})` },
        React.createElement('path', {
          d: 'M12 4L3 15L12 26',
          stroke: accentColor,
          strokeWidth: '2.8',
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }),
        React.createElement('path', {
          d: 'M24 4L33 15L24 26',
          stroke: accentColor,
          strokeWidth: '2.8',
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }),
        React.createElement('path', {
          d: 'M20 2L16 28',
          stroke: primaryColor,
          strokeWidth: '2',
          strokeLinecap: 'round',
        })
      ),
      // Texto "AI Recruiter"
      React.createElement(
        'text',
        {
          x: 30 * iconScale + 14,
          y: fullHeight / 2 + Number(fontSize) * 0.35,
          fill: primaryColor,
          fontSize,
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          fontWeight: '700',
          letterSpacing: '-0.02em',
        },
        'AI Recruiter'
      )
    )
  );
}
